// src/components/forwardsCalculator/ForwardColumn.js
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import * as con from '../../GlobalConstants'
import { GenericCell } from '../../templates/tables/Cells'
import { TextAreaElement } from '../../templates/FormElement'
import Modal from '../../templates/popups/Modal'
import { Button } from '@blueprintjs/core'
import Toggle from 'react-toggle'
import { COUNTERPARTIES_NAMES, translateParameter } from '../../utils/translateFunctions'
import { addFWDFromCalculator } from '../../store/slices/transactionsSlice'
import { createNotification } from '../../templates/notifications/Notifications'
import { formatDate, parseDate } from '../../utils/dateFunctions'

const ForwardColumn = ({ col, derived, refSpot, handlers, row, evenRow }) => {
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)
  const [comment, setComment]     = useState('')

  // Clase de fondo para wrappers con toggle — debe coincidir con la fila
  const rowBg = evenRow ? 'even-row' : 'uneven-row'

  const handleAddCoverage = () => {
    const values = {
      [con.OPENING_DATE]:    formatDate(parseDate(col.executionDate)),
      [con.EXPIRATION_DATE]: formatDate(parseDate(col.expirationDate)),
      [con.AMOUNT]:          col.nominal,
      [con.RATE]:            col.bankRate,
      [con.OPENING_SPOT]:    col.bankSpot,
      [con.COMMENT]:         comment || '',
      [con.COUNTERPARTY]:    col.counterparty,
      [con.COVERAGE_TYPE]:   col.type,
      [con.STATE]:           con.ACTIVE,
    }
    dispatch(addFWDFromCalculator({ values }))
      .unwrap()
      .then(() => {
        setShowModal(false)
        setComment('')
        createNotification('Cobertura agregada correctamente')
      })
  }

  const cell = (() => {
    switch (row) {

      case 'counterparty':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MULTIPLE_SELECTION} editing
            value={COUNTERPARTIES_NAMES[col.counterparty] || ''}
            options={['', ...Object.values(COUNTERPARTIES_NAMES)]}
            setValue={handlers.setCounterparty}
          />
        )

      case 'type':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MULTIPLE_SELECTION} editing
            options={[translateParameter(con.BUY), translateParameter(con.SELL)]}
            value={translateParameter(col.type)}
            setValue={handlers.setType}
          />
        )

      case 'executionDate':
        return (
          <GenericCell evenRow={evenRow} cellType={con.DATE} editing
            value={col.executionDate}
            setValue={handlers.setExecutionDate}
          />
        )

      case 'spot':
        return (
          <div className={`flex-celda ${rowBg}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <div style={{ flex: "0 0 75%", minWidth: 0, overflow: "hidden" }}>
              <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
                editing={col.spotManual} allowDecimals
                value={col.spotManual ? col.spot : refSpot}
                setValue={handlers.setSpot}
              />
            </div>
            <Toggle checked={col.spotManual} icons={false} className="custom-toggle" style={{ flex: "0 0 25%" }}
              onChange={(e) => handlers.setSpotManual(e.target.checked)}
            />
          </div>
        )

      case 'nominal':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_USD}
            editing allowDecimals
            value={col.nominal}
            setValue={handlers.setNominal}
          />
        )

      case 'expiryDays':
        return (
          <div className={`flex-celda ${rowBg}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <div style={{ flex: "0 0 75%", minWidth: 0, overflow: "hidden" }}>
              <GenericCell evenRow={evenRow} cellType={con.NUMERIC}
                editing={col.maturityInput === con.DAYS}
                value={col.expiryDays}
                setValue={(val) => handlers.setMaturity(con.DAYS, val)}
              />
            </div>
            <Toggle icons={false} className="custom-toggle" style={{ flex: "0 0 25%" }}
              checked={col.maturityInput === con.DAYS}
              onChange={() => handlers.setMaturityInput(col.maturityInput === con.DAYS ? 'date' : con.DAYS)}
            />
          </div>
        )

      case 'expirationDate':
        return (
          <div className={`flex-celda ${rowBg}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <div style={{ flex: "0 0 75%", minWidth: 0, overflow: "hidden" }}>
              <GenericCell evenRow={evenRow} cellType={con.DATE}
                editing={col.maturityInput === 'date'}
                value={col.expirationDate}
                setValue={(val) => handlers.setMaturity('date', val)}
              />
            </div>
            <Toggle icons={false} className="custom-toggle" style={{ flex: "0 0 25%" }}
              checked={col.maturityInput === 'date'}
              onChange={() => handlers.setMaturityInput(col.maturityInput === 'date' ? con.DAYS : 'date')}
            />
          </div>
        )

      case 'refRate':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
            editing={false} allowDecimals value={derived.refRate}
          />
        )

      case 'refDevaluation':
        return (
          <GenericCell evenRow={evenRow} cellType={con.PERCENTAGE}
            editing={false} decimalScale={2} value={derived.refDevaluation}
          />
        )

      case 'bankSpot':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
            editing allowDecimals
            value={col.bankSpot}
            setValue={handlers.setBankSpot}
          />
        )

      case 'bankRate':
        return (
          <div className={`flex-celda ${rowBg}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <div style={{ flex: "0 0 75%", minWidth: 0, overflow: "hidden" }}>
              <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
                editing={col.bankInput === 'rate'} allowDecimals
                value={col.bankRate}
                setValue={(val) => handlers.setBankQuote('rate', val)}
              />
            </div>
            <Toggle icons={false} className="custom-toggle" style={{ flex: "0 0 25%" }}
              checked={col.bankInput === 'rate'}
              onChange={() => handlers.setBankInputToggle(col.bankInput === 'rate' ? 'devaluation' : 'rate')}
            />
          </div>
        )

      case 'bankDevaluation':
        return (
          <div className={`flex-celda ${rowBg}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <div style={{ flex: "0 0 75%", minWidth: 0, overflow: "hidden" }}>
              <GenericCell evenRow={evenRow} cellType={con.PERCENTAGE}
                editing={col.bankInput === 'devaluation'} decimalScale={2}
                value={col.bankDevaluation}
                setValue={(val) => handlers.setBankQuote('devaluation', val)}
              />
            </div>
            <Toggle icons={false} className="custom-toggle" style={{ flex: "0 0 25%" }}
              checked={col.bankInput === 'devaluation'}
              onChange={() => handlers.setBankInputToggle(col.bankInput === 'devaluation' ? 'rate' : 'devaluation')}
            />
          </div>
        )

      case 'fwdPoints':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
            editing={false} allowDecimals value={derived.fwdPoints}
          />
        )

      case 'diffFwdPoints':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
            editing={false} allowDecimals value={derived.diffFwdPoints}
          />
        )

      case 'diffCurvePxD':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
            editing={false} allowDecimals value={derived.diffCurvePxD}
          />
        )

      case 'diffSpotPxD':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
            editing={false} allowDecimals value={derived.diffSpotPxD}
          />
        )

      case 'diffTotalPxD':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
            editing={false} allowDecimals value={derived.diffTotalPxD}
          />
        )

      case 'diffTotal':
        return (
          <GenericCell evenRow={evenRow} cellType={con.MONEY} currency={con.MONEY_COP}
            editing={false} allowDecimals value={derived.diffTotal}
          />
        )

      case 'commands':
        return (
          <div className={`flex-celda ${rowBg}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button text="Agregar a Coberturas" icon="plus" onClick={() => setShowModal(true)} />
            <Modal show={showModal} doAccept={null} showCancel={false} doCancel={() => setShowModal(false)}>
              <div>
                <h3>Agregar Cobertura</h3>
                <TextAreaElement name="Comentario" value={comment} setValue={setComment} />
                <div className="confirmCancelParameterBox" style={{ marginTop: '15px' }}>
                  <Button icon="upload" text="Agregar Cobertura" onClick={handleAddCoverage} />
                  <Button icon="cross" text="Cancelar" onClick={() => setShowModal(false)} />
                </div>
              </div>
            </Modal>
          </div>
        )

      default:
        return null
    }
  })()

  return <td>{cell}</td>
}

export default ForwardColumn